import React from "react";
import PropTypes from "prop-types";
import { IconButton } from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";

const ELLIPSIS = "ellipsis";

// Pages at or below this count are shown in full without ellipsis.
const FULL_PAGE_THRESHOLD = 4;

const range = (from, to) =>
    Array.from({ length: to - from + 1 }, (_, i) => i + from);

/**
 * Build the sequence of page tokens to render, inserting ellipsis tokens
 * where a run of 2+ pages is collapsed. Always includes the first and last
 * page plus a window of `siblings` pages either side of the current page.
 *
 * Returns an array of number | "ellipsis".
 */
export function buildPageWindow(current, last, siblings = 1) {
    if (last <= 1) {
        return last === 1 ? [1] : [];
    }
    if (last <= FULL_PAGE_THRESHOLD) {
        return range(1, last);
    }

    const pages = new Set([
        1,
        last,
        current,
        ...range(current - siblings, current + siblings),
    ]);

    return [...pages]
        .filter((p) => p >= 1 && p <= last)
        .sort((a, b) => a - b)
        .reduce((acc, page, i, arr) => {
            if (i > 0 && page - arr[i - 1] > 2) {
                acc.push(ELLIPSIS);
            }
            acc.push(page);
            return acc;
        }, []);
}

/**
 * Pagination control composed from Material Tailwind IconButton and the
 * project's BEM button classes. `currentPage` and `lastPage` are plain
 * numbers — server-authoritative (from the backend pager) or client-computed
 * (e.g. keyphrases slicing).
 */
export default function Pagination({ currentPage, lastPage, onPageChange }) {
    if (!currentPage || !lastPage) {
        return null;
    }

    const isFirst = currentPage <= 1;
    const isLast = currentPage >= lastPage;
    const tokens = buildPageWindow(currentPage, lastPage);

    return (
        <div className="flex justify-end items-center mt-2 gap-2 my-0">
            <IconButton
                type="button"
                aria-label="First page"
                className="btn-icon--primary"
                disabled={isFirst}
                onClick={() => onPageChange(1)}
            >
                <MaterialIcon name="first_page" size="sm" />
            </IconButton>
            <IconButton
                type="button"
                aria-label="Previous page"
                className="btn-icon--primary"
                disabled={isFirst}
                onClick={() => onPageChange(currentPage - 1)}
            >
                <MaterialIcon name="arrow_back_ios_new" size="sm" />
            </IconButton>
            {tokens.map((token, i) =>
                token === ELLIPSIS ? (
                    <span
                        key={`ellipsis-${i}`}
                        className="px-1 text-gray-500 select-none"
                    >
                        &hellip;
                    </span>
                ) : (
                    <IconButton
                        key={token}
                        type="button"
                        aria-label={`Page ${token}`}
                        aria-current={token === currentPage ? "page" : undefined}
                        className={
                            token === currentPage
                                ? "btn-icon--secondary"
                                : "btn-icon--primary"
                        }
                        onClick={() => onPageChange(token)}
                    >
                        {token}
                    </IconButton>
                ),
            )}
            <IconButton
                type="button"
                aria-label="Next page"
                className="btn-icon--primary"
                disabled={isLast}
                onClick={() => onPageChange(currentPage + 1)}
            >
                <MaterialIcon name="arrow_forward_ios" size="sm" />
            </IconButton>
            <IconButton
                type="button"
                aria-label="Last page"
                className="btn-icon--primary"
                disabled={isLast}
                onClick={() => onPageChange(lastPage)}
            >
                <MaterialIcon name="last_page" size="sm" />
            </IconButton>
        </div>
    );
}

Pagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    lastPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};
